<template>
    <div class="position-relative">
        <div class="position-absolute w-100 h-100" v-if="load">
            <load :load="true" class="position-relative" />
        </div>
        <div class="row">
            <div class="col-12">
                <legend class='form-legend w-100'>
                    <font-awesome-icon icon='fa-solid fa-address-book' class='me-1' />
                    Informações de Contato
                </legend>
            </div>

            <div class="col-md-6 col-lg-4">
                <text-input
                    :required="true"
                    class="w-100"
                    label="Nome"
                    v-model="formData.contact_name"
                    :disabled="loadBtn || disabled"
                    :errors="errors?.contact_name"
                />
            </div>

            <div class="col-md-6 col-lg-4">
                <text-input
                    :required="true"
                    class="w-100"
                    label="Email"
                    v-model="formData.email"
                    :disabled="loadBtn || disabled"
                    :errors="errors?.email"
                />
            </div>

            <div class="col-md-6 col-lg-4">
                <text-input
                    ref="inputPhoneNumberRef"
                    :required="true"
                    class="w-100"
                    label="Telefone"
                    v-model="formData.phone_number"
                    :disabled="loadBtn || disabled"
                    :errors="errors?.phone_number"
                    v-maska="['(##) ####-####', '(##) #####-####']"
                />
            </div>

            <div class="col-12">
                <legend class='form-legend w-100'>
                    <font-awesome-icon icon="fa-solid fa-building" class='me-1' />
                    Informações da Empresa
                </legend>
            </div>

            <div class="col-md-6">
                <text-input
                    ref="inputCnpjRef"
                    class="w-100"
                    label="CNPJ"
                    v-model="formData.cnpj"
                    :disabled="loadBtn || disabled"
                    :required="true"
                    :errors="cnpjErrors ?? errors?.cnpj"
                    v-maska='"##.###.###/####-##"'
                    @change="onCnpj"
                />
            </div>

            <div class="col-md-6">
                <text-input
                    :required="true"
                    class="w-100"
                    label="Razão Social"
                    v-model="formData.social_name"
                    :disabled="loadBtn || disabled"
                    :errors="errors?.social_name"
                />
            </div>

            <div class="col-md-6">
                <text-input
                    :required="true"
                    class="w-100"
                    label="Nome Fantasia"
                    v-model="formData.name"
                    :disabled="loadBtn || disabled"
                    :errors="errors?.name"
                />
            </div>

            <div class="col-md-6 col-lg-3">
                <select-input
                    label='CNAE'
                    :required="true"
                    v-model="formData.cnae_id"
                    :loading='loadCnaes'
                    :options='cnaes'
                    :option-custom-label='cnaeCustomLabel'
                    option-label='description'
                    placeholder='Pesquise...'
                    :errors="errors?.cnae_id"
                    @onSearch='onSearchCnae($event)'
                    :truncate="true"
                    :disabled="loadBtn || disabled"
                />
            </div>

            <div class="col-md-6 col-lg-3">
                <select-input
                    :required="true"
                    label="Porte da Empresa"
                    v-model="formData.size"
                    :options="optionsCompanySize"
                    :errors="errors?.size"
                    :disabled="loadBtn || disabled"
                />
            </div>

            <div class="col-12">
                <legend class='form-legend w-100'>
                    <font-awesome-icon icon='fa-solid fa-location-dot' class='me-1' />
                    Endereço
                </legend>
            </div>

            <div class='col-lg-4'>
                <text-input
                    ref="inputCepRef"
                    :required="true"
                    label='CEP'
                    placeholder='_____-___'
                    v-model='formData.address.cep'
                    v-maska='"#####-###"'
                    :errors='errors ? errors["address.cep"] : null'
                    @onChange='onCep'
                    :disabled="loadBtn || disabled"
                />
            </div>
            <div class='col-lg-4'>
                <select-input
                    :required="true"
                    label='Estado'
                    v-model='formData.address.state_id'
                    :options='states'
                    @onChange='onState'
                    :errors='errors ? errors["address.state_id"] : null'
                    :disabled="loadBtn || disabled"
                    :loading="loadStates"
                />
            </div>
            <div class='col-lg-4'>
                <select-input
                    :required="true"
                    label='Cidade'
                    v-model='formData.address.city_id'
                    :options='cities'
                    :errors='errors ? errors["address.city_id"] : null'
                    :disabled="loadBtn || disabled"
                    :loading="loadCities"
                />
            </div>

            <div class="col-12" v-if="!!formData?.custom_fields?.length">
                <legend class='form-legend w-100'>
                    <font-awesome-icon icon='fa-solid fa-plus' class='me-1' />
                    Informações de Complementares
                </legend>
            </div>

            <div v-for="(field, index) in formData?.custom_fields ?? []"
                 :key="index"
                 class="col-12"
            >
                <custom-field
                    v-model="field.value"
                    :label="field.label"
                    :type="field.type"
                    :required="field.required"
                    :options="field.options"
                    :description="field.description"
                    :example="field.example"
                    :errors="errors ? errors[slugify(field.label)] : null"
                    :disabled="loadBtn || disabled"
                />
            </div>
        </div>
    </div>
</template>
<script setup>
    import TextInput from '@/components/TextInput.vue';
    import { computed, onMounted, ref, watch } from 'vue';
    import SelectInput from '@/components/SelectInput.vue';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
    import { handleAxiosError, justNumbers, mask, match, slugify } from '@/helpers';
    import Load from '@/components/Load.vue';
    import { useStore } from 'vuex';
    import debounce from 'lodash.debounce';
    import CustomField from '@/components/CustomField.vue';

    const store = useStore();
    const states = ref([]);
    const cities = ref([]);
    const cnaes = ref([]);
    const loadCnaes = ref(false);
    const loadStates = ref(false);
    const loadCities = ref(false);
    const cnpjErrors = ref(null);

    const inputCnpjRef = ref(null);
    const inputPhoneNumberRef = ref(null);
    const inputCepRef = ref(null);

    const emit = defineEmits([
        'update:modelValue',
        'update:loadPage',
        'onSave',
        'onCancel',
        'onSetFormData',
        'onUnavaibleCnpj'
    ]);

    const props = defineProps({
        modelValue: Object,
        errors: Object,
        loadBtn: Boolean,
        loadPage: Boolean,
        isVisible: Boolean,
        callbackChangeCnpj: Function,
        disabled: { type: Boolean, default: false },
        isUniqueCnpj: { type: Boolean, default: true },
        segmentedSurveyUuid: { type: [String, null], default: null, required: false },
        customFields: { type: Array, default: [] }
    });

    const formData = computed({
        get: () => props.modelValue,
        set: value => emit('update:modelValue', value)
    });

    const load = computed({
        get: () => props.loadPage,
        set: value => emit('update:loadPage', value)
    });

    const optionsCompanySize = ref([
        { id: 'P', name: 'Pequeno' },
        { id: 'M', name: 'Médio' },
        { id: 'G', name: 'Grande' }
    ]);

    const onState = async (value) => {
        formData.value.address.city_id = null;

        if (value != null) {
            load.value = true;
            await getCities(value);
            load.value = false;
        }
    };

    const getStates = async () => {
        try {
            loadStates.value = true;
            const { data } = await axios.get('segmented-survey/states');
            states.value = data;
        } catch (e) {
            handleAxiosError(e);
        } finally {
            loadStates.value = false;
        }
    };

    const getCities = async (stateId) => {
        try {
            loadCities.value = true;
            const { data } = await axios.get(`segmented-survey/cities?where[state_id]=${stateId}`);
            cities.value = data;
        } catch (e) {
            handleAxiosError(e);
        } finally {
            loadCities.value = false;
        }
    };

    const onCep = async (value) => {
        if (value && value.length === 9) {
            try {
                load.value = true;
                const addressData = await store.dispatch('getCep', value);
                if (addressData) {
                    const state = states.value?.find((state) => state.initials === addressData.uf);
                    formData.value.address.address = addressData.logradouro;
                    formData.value.address.district = addressData.bairro;

                    if (state) {
                        await getCities(state.id);
                        formData.value.address.state_id = state.id;
                        const city = cities.value.find((city) => city.ibge_id === addressData.ibge);
                        if (city) {
                            formData.value.address.city_id = city.id;
                        }
                    }
                }
            } catch (e) {
                handleAxiosError(e);
            } finally {
                load.value = false;
            }
        }
    };

    const onSearchCnae = debounce(async (value) => {
        if (value && value.length >= 3) {
            loadCnaes.value = true;
            await getCnaes(`whereLike[cnae]=${value}&orWhereLike[description]=${value}`);
            loadCnaes.value = false;
        }
    }, 600);

    const getCnaes = async (search, index) => {
        try {
            const { data } = await axios.get(`segmented-survey/cnaes?${search}`);
            cnaes.value = data;
        } catch (e) {
            handleAxiosError(e);
        }
    };

    const findCnae = async (cnaeId) => {
        try {
            const { data } = await axios.get(`segmented-survey/cnaes?where[id]=${cnaeId}`);
            cnaes.value = data;
        } catch (e) {
            handleAxiosError(e);
        }
    };

    const cnaeCustomLabel = ({ description, cnae }) => {
        return `${cnae} - ${description}`;
    };

    const onCnpj = async (event) => {
        const cnpj = event?.target?.value ? justNumbers(event?.target?.value) : '';

        if (!cnpj.length) {
            return;
        }

        const oldData = { ...formData.value };
        resetComponentData();

        try {
            load.value = true;
            let endpoint = `segmented-survey/check-cnpj/?cnpj=${cnpj}`;

            if (formData.value?.id) {
                endpoint += '&id=' + formData.value.id;
            }
            if (props.segmentedSurveyUuid) {
                endpoint += `&token=${props.segmentedSurveyUuid}`;
            }

            const { data } = await axios.get(endpoint);

            setFormData({
                ...data,
                contact_name: oldData?.contact_name,
                email: oldData?.email,
                phone_number: oldData?.phone_number
            });

        } catch (e) {
            if (e?.response?.status === 422) {
                cnpjErrors.value = e?.response?.data?.errors?.cnpj;
                cnpjErrors.value.forEach((error) => {
                    if (error === 'O CNPJ informado já está cadastrado.') {
                        emit('onUnavaibleCnpj', formData.value.cnpj);
                    }
                });
            }
            handleAxiosError(e);
            setFormData(oldData, false);
        } finally {
            load.value = false;
        }
    };

    const resetComponentData = () => {
        cities.value = [];
        cnaes.value = [];
        setFormData(null);
    };

    const setFormData = (value = null, clearErrors = true) => {

        formData.value.id = value?.id;
        formData.value.contact_name = value?.contact_name;
        formData.value.name = value?.name ?? value?.social_name;
        formData.value.social_name = value?.social_name;
        formData.value.email = value?.email;
        formData.value.size = value?.size;
        formData.value.cnae_id = value?.cnae_id;
        formData.value.address.state_id = value?.address?.state_id;
        formData.value.address.city_id = value?.address?.city_id;

        formData.value.cnpj = null;
        formData.value.phone_number = null;
        formData.value.address.cep = null;

        if (value?.cnpj) {
            const cnpj = justNumbers(value?.cnpj);
            formData.value.cnpj = mask(cnpj, '##.###.###/####-##');
        }

        if (value?.phone_number) {
            const phoneNumber = justNumbers(value?.phone_number);
            formData.value.phone_number = phoneNumber.toString().length < 11
                ? mask(phoneNumber, '(##) ####-####')
                : mask(phoneNumber, '(##) #####-####');
        }

        if (value?.address?.cep) {
            const cep = justNumbers(value?.address?.cep);
            formData.value.address.cep = mask(cep, '#####-###');
        }
        
        if (!!value?.custom_fields?.length) {
            formData.value.custom_fields.forEach((field) => {
                const customField = value?.custom_fields?.find((customField) => customField.id === field.id);
                
                if (customField) {
                    field.value = buildCustomFieldValue(customField);
                }
            });
        }

        if (clearErrors) {
            cnpjErrors.value = null;
        }

        emit('onSetFormData', value);
    };


    const buildWeights = (weights, options) => {
        const data = [];
        for (const option of options) {
            const value = !!weights
                ? weights.find(item => item?.name === option)
                : null;

            if (value) data.push(value);
            else data.push({ name: option, value: 0 });
        }
        return data;
    };

    const buildCustomFieldValue = (field) => {
        const value = match(field.type, {
            'peso': () => buildWeights(field.value, field.options),
            'boolean': () => field.value ?? false
        }, () => field.value);
        
        return value();
    };
    
    defineExpose({
        resetComponentData,
        setFormData
    });

    onMounted(async () => {
        if (inputCnpjRef.value?.inputRef?.focus) {
            inputCnpjRef.value.inputRef.focus();
        }
        await getStates();
    });

    watch(() => props.customFields, (value) => {
        if (!value?.length) return;
        formData.value.custom_fields = (structuredClone(value))?.map((field) => ({
            id: field.id,
            label: field.label,
            type: field.type,
            options: field.options,
            required: field.is_required ?? false,
            description: field.description,
            example: field.example,
            value: buildCustomFieldValue(field)
        }));
    });

    watch(() => formData.value.address.state_id, async (stateId) => {
        if (!!stateId) await getCities(stateId);
    });
    
    watch(() => formData.value.cnae_id, async (cnaeId) => {
        if (!!cnaeId) await findCnae(cnaeId);
    });

</script>
